import to from 'await-to-js';
import byteSize from 'byte-size';
import fs from 'fs';
import { homedir } from 'os';
import path from 'path';
import { Logger } from '~/util/logger';
import { tryParse } from '~/util/tryparse';

const userHomeDir = homedir();
export const appRootDir = path.join(userHomeDir, '.contensis/');
export const assetsFolderRoot = path.join(appRootDir, 'cache/');

export const readJsonFile = <T>(filePath: string) => {
  const file = readFile(filePath);
  if (file) return tryParse(file) as T | string;
  return undefined;
};

export const readFile = (filePath: string) => {
  const directoryPath = localPath(filePath);
  if (fs.existsSync(directoryPath)) {
    const file = fs.readFileSync(directoryPath, 'utf8');
    return file;
  } else {
    return undefined;
  }
};

export const readFiles = (directory: string) => {
  const directoryPath = localPath(directory);
  if (fs.existsSync(directoryPath)) {
    const files = fs.readdirSync(directoryPath);
    return files;
  } else {
    fs.mkdirSync(directoryPath, { recursive: true });
    return [];
  }
};

export const writeFile = (filePath: string, content: string) => {
  const directoryPath = localPath(filePath);
  fs.writeFileSync(directoryPath, content, { encoding: 'utf-8' });
};

export const writeStream = (filePath: string) => {
  return fs.createWriteStream(filePath);
};

export const checkDir = (filePath: string) => {
  const directoryPath = path.dirname(localPath(filePath));
  if (!fs.existsSync(directoryPath))
    fs.mkdirSync(directoryPath, { recursive: true });
};

export const checkFile = (filePath: string) => {
  return fs.existsSync(localPath(filePath));
};

export const copyFile = (from: string, to: string) => {
  return fs.copyFileSync(from, to);
};

export const localPath = (filePath: string) =>
  path.isAbsolute(filePath) ? filePath : path.join(appRootDir, filePath);

export const getFilesListDeep = (
  dirPath: string,
  arrayOfFiles: string[] = []
) => {
  const files = fs.readdirSync(dirPath);

  for (const file of files) {
    if (fs.statSync(dirPath + '/' + file).isDirectory()) {
      arrayOfFiles = getFilesListDeep(dirPath + '/' + file, arrayOfFiles);
    } else {
      arrayOfFiles.push(path.join(dirPath, '/', file));
    }
  }

  return arrayOfFiles;
};

export const dirSize = async (directory: string) => {
  const files = getFilesListDeep(localPath(directory));
  const stats = files.map((file) => fs.statSync(file));

  const totalBytes = (await Promise.all(stats)).reduce(
    (accumulator, { size }) => accumulator + size,
    0
  );

  return byteSize(totalBytes).toString();
};

export const cacheSize = async (): Promise<
  [AppError | null, string | undefined]
> => await to(dirSize(assetsFolderRoot));

export const cachePreview = async (): Promise<
  [AppError | undefined, { [folder: string]: string } | undefined]
> => {
  try {
    const files = fs.readdirSync(assetsFolderRoot);
    return [
      undefined,
      Object.fromEntries(
        await Promise.all(
          files.map(async (file) => [
            path.basename(file),
            await dirSize(`${assetsFolderRoot}/${file}`),
          ])
        )
      ),
    ];
  } catch (ex: any) {
    Logger.error(`[cache] ${ex?.message}`);
    return [ex, undefined];
  }
};
